import './game.css';
import '../party/party.css';
import { relatedToolsMarkup, siteBrandMarkup } from '../site-chrome';
import { kakaoCardShareMessage, preloadKakaoShare } from '../kakao-share';
import { PartyNetwork, type PartyConnectionState } from '../party/network';
import {
  normalizePartyRoomCode,
  type PartyPublicRoomSummary,
  type PartyRoomClosedReason,
  type PartyRoomView,
  type PartyServerMessage,
} from '../party/protocol';
import {
  PARTY_PLAYER_COLORS,
  copyPartyText,
  escapePartyHtml,
  inviteRoomFromLocation,
  partyEntryMarkup,
  partyInviteUrl,
  partyLobbyMarkup,
  savedPartyNickname,
  savePartyNickname,
  sharePartyInvite,
  updatePartyLobbyAutoStart,
  type PartyEntryTab,
} from '../party/ui';
import {
  FORTRESS_MAX_POWER,
  FORTRESS_MIN_POWER,
  FORTRESS_MOVE_PER_TURN,
  FORTRESS_TERRAIN_WIDTH,
  FORTRESS_WEAPONS,
  applyFortressTerrainPatch,
  fortressMapSpec,
  fortressPowerAtElapsed,
  fortressWeaponSpec,
  isFortressView,
  type FortressMapId,
  type FortressPlayerView,
  type FortressView,
  type FortressWeaponId,
} from './core';
import {
  FORTRESS_MOVE_REPEAT_MILLISECONDS,
  fortressArenaProjection,
  fortressAdjustAngle,
  fortressAimElevation,
  fortressAngleFacing,
  fortressKeyboardControl,
  fortressMovementPosition,
  type FortressDirection,
} from './controls';

const GAME_PATH = '/games/kong-fortress/';
const GAME_TITLE = '콩포트리스';
const ARENA_WIDTH = 1200;
const ARENA_HEIGHT = 700;

interface FortressMapTheme {
  readonly sky: readonly [string, string, string];
  readonly sun: string;
  readonly backdrop: string;
  readonly earth: readonly [string, string];
  readonly ridge: string;
  readonly grass: string;
  readonly cloud: string;
  readonly badge: string;
  readonly badgeText: string;
}

const FORTRESS_MAP_THEMES: Readonly<Record<FortressMapId, FortressMapTheme>> = {
  'rolling-hills': {
    sky: ['#67d7d3', '#c9f1e5', '#79cfe0'],
    sun: 'rgba(255,229,123,.9)',
    backdrop: 'rgba(36,120,133,.22)',
    earth: ['#a96b3f', '#6f432d'],
    ridge: '#17304d',
    grass: '#78c85d',
    cloud: 'rgba(255,255,255,.75)',
    badge: 'rgba(23,48,77,.82)',
    badgeText: '#ffffff',
  },
  'central-highland': {
    sky: ['#45aee5', '#bce8f4', '#76c9df'],
    sun: 'rgba(255,232,142,.92)',
    backdrop: 'rgba(41,104,153,.24)',
    earth: ['#9c6544', '#603f35'],
    ridge: '#183451',
    grass: '#8bcc65',
    cloud: 'rgba(255,255,255,.78)',
    badge: 'rgba(19,58,91,.84)',
    badgeText: '#ffffff',
  },
  'crosswind-ridges': {
    sky: ['#8bcfe8', '#eaf1ca', '#efbf83'],
    sun: 'rgba(255,242,177,.94)',
    backdrop: 'rgba(94,126,117,.23)',
    earth: ['#b16d42', '#71402f'],
    ridge: '#353a4e',
    grass: '#9ac956',
    cloud: 'rgba(255,250,235,.72)',
    badge: 'rgba(53,58,78,.84)',
    badgeText: '#fff8e8',
  },
  'twin-peaks': {
    sky: ['#8578d6', '#d7cbf4', '#91d8cf'],
    sun: 'rgba(255,237,190,.9)',
    backdrop: 'rgba(68,69,130,.24)',
    earth: ['#8e5c60', '#493746'],
    ridge: '#292744',
    grass: '#85d68f',
    cloud: 'rgba(250,246,255,.7)',
    badge: 'rgba(41,39,68,.86)',
    badgeText: '#fffaff',
  },
  'moon-basin': {
    sky: ['#17294d', '#355a82', '#82aebd'],
    sun: 'rgba(250,241,205,.95)',
    backdrop: 'rgba(22,39,75,.38)',
    earth: ['#69526d', '#302c42'],
    ridge: '#111b31',
    grass: '#82cdb3',
    cloud: 'rgba(223,236,245,.5)',
    badge: 'rgba(12,22,43,.88)',
    badgeText: '#f9f1d2',
  },
  'tilted-land': {
    sky: ['#ff9d83', '#ffd2a2', '#8dd4cf'],
    sun: 'rgba(255,245,177,.94)',
    backdrop: 'rgba(111,100,124,.23)',
    earth: ['#a95c4d', '#61383a'],
    ridge: '#41334a',
    grass: '#a4d96c',
    cloud: 'rgba(255,247,229,.68)',
    badge: 'rgba(65,51,74,.84)',
    badgeText: '#fff7e8',
  },
};

type MaterializedFortressView = Omit<FortressView, 'terrain'> & { readonly terrain: readonly number[] };

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`${selector} 요소를 찾지 못했습니다.`);
  return element;
}

function connectionCopy(state: PartyConnectionState): string {
  if (state === 'connected') return '서버 연결됨';
  if (state === 'connecting') return '연결하는 중';
  if (state === 'reconnecting') return '재접속하는 중';
  return '연결 대기';
}

function closedReasonCopy(reason: PartyRoomClosedReason): string {
  if (reason === 'left') return '방에서 나왔습니다.';
  if (reason === 'room-expired') return '오랫동안 사용하지 않아 방이 종료됐습니다.';
  return '서버 점검으로 방이 종료됐습니다. 잠시 후 다시 시도해 주세요.';
}

function mix(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

function windCopy(windMps: number): string {
  if (Math.abs(windMps) < 0.05) return '바람 없음 0.0 m/s';
  return `${windMps > 0 ? '→' : '←'} ${Math.abs(windMps).toFixed(1)} m/s`;
}

function weaponCopy(weapon: FortressWeaponId): string {
  return fortressWeaponSpec(weapon).shortLabel;
}

export function mountKongFortress(): void {
  preloadKakaoShare();
  const app = requireElement<HTMLDivElement>('#app');
  document.documentElement.classList.add('party-page', 'fortress-page');
  document.title = `${GAME_TITLE} | 오늘툴`;

  app.innerHTML = `
    <div class="party-shell fortress-shell">
      <header class="site-brand-header party-header">
        ${siteBrandMarkup()}
        <span class="party-header-badge">2–4인 · 턴제 포격</span>
      </header>

      <main>
        <section class="party-intro fortress-intro">
          <span>AIM · WIND · BOOM!</span>
          <h1>콩포트리스</h1>
          <p>인원이 늘수록 넓어지는 6가지 섬에서 코코넛과 정밀 미사일을 골라 발사하세요.<br />바람과 각도를 읽고 마지막까지 살아남는 파도콩이 승리!</p>
        </section>

        <section class="party-card fortress-card" aria-label="콩포트리스 게임">
          <div class="party-card-bar">
            <span id="party-connection" class="party-connection offline">연결 대기</span>
            <div id="party-room-mini" class="party-room-mini" hidden>
              <span>방</span><strong id="party-room-mini-code"></strong>
              <button type="button" data-party-action="copy-code">코드 복사</button>
            </div>
          </div>
          <div id="party-surface" class="party-surface fortress-surface"></div>
        </section>

        <section class="party-help" aria-label="게임 방법">
          <article><strong>1. 움직이고 바람을 읽어요</strong><p>←·→ 또는 A·D를 누르고 있으면 턴마다 최대 ${FORTRESS_MOVE_PER_TURN}칸 이동해요. 좌우 키로 바라보는 방향을 바꾸고, 맵 상단에서 바람을 확인하세요.</p></article>
          <article><strong>2. 포탄을 골라요</strong><p>코코넛은 넓게 터지고, 정밀 미사일은 범위가 좁지만 정확히 맞히면 더 큰 피해를 줘요.</p></article>
          <article><strong>3. 짧은 가이드로 조준해요</strong><p>↑·W는 포신을 올리고 ↓·S는 내려요. 스페이스바를 누르고 있다 떼면 발사해요. 착지점은 표시하지 않아요.</p></article>
        </section>

        ${relatedToolsMarkup('kong-fortress')}
      </main>

      <footer class="party-footer">
        <a href="/privacy/">개인정보처리방침</a>
        <a href="/terms/">이용약관</a>
        <span>방과 경기 기록은 서버 메모리에만 잠시 보관돼요.</span>
      </footer>
    </div>`;

  const surface = requireElement<HTMLDivElement>('#party-surface');
  const connectionBadge = requireElement<HTMLSpanElement>('#party-connection');
  const roomMini = requireElement<HTMLDivElement>('#party-room-mini');
  const roomMiniCode = requireElement<HTMLElement>('#party-room-mini-code');
  const incomingRoom = inviteRoomFromLocation();

  let connection: PartyConnectionState = 'offline';
  let room: PartyRoomView | null = null;
  let currentView: MaterializedFortressView | null = null;
  let previousProjectile: { x: number; y: number } | null = null;
  let renderedProjectile: { x: number; y: number } | null = null;
  let projectileSnapshotAt = performance.now();
  let serverClockDelta = 0;
  let notice = '';
  let currentSurfaceKey = '';
  let entryTab: PartyEntryTab = 'private';
  let entryNickname = savedPartyNickname();
  let publicRooms: readonly PartyPublicRoomSummary[] = [];
  let publicRoomsLoaded = false;
  let arenaCanvas: HTMLCanvasElement | null = null;
  let arenaContext: CanvasRenderingContext2D | null = null;
  let angle = 45;
  let aimFacing: FortressDirection = 1;
  const playerMovements = new Map<string, { from: { x: number; y: number }; startedAt: number }>();
  let power = FORTRESS_MIN_POWER;
  let selectedWeapon: FortressWeaponId = 'coconut';
  let aimTurnId = -1;
  let firePending = false;
  let movementPending = false;
  let chargeStartedAt: number | null = null;
  let chargePointerId: number | null = null;
  let movementHold: { direction: -1 | 1; nextAt: number } | null = null;
  let angleHold: { direction: -1 | 1; nextAt: number } | null = null;
  let controlPointerId: number | null = null;
  let announcedPhaseId = -1;

  const network = new PartyNetwork('kong-fortress', {
    onConnection(nextConnection) {
      connection = nextConnection;
      if (nextConnection !== 'connected') cancelActiveInputs();
      updateConnection();
      updateReconnectOverlay();
      if (!room) renderSurface(true);
      else updateGameHud();
    },
    onMessage(message) {
      handleMessage(message);
    },
  });

  function ownRoomPlayer() {
    return room?.players.find((player) => player.id === network.playerId) ?? null;
  }

  function ownGamePlayer() {
    return currentView?.players.find((player) => player.id === network.playerId) ?? null;
  }

  function playerName(playerId: string | null): string {
    if (!playerId) return '파도콩';
    return currentView?.players.find((player) => player.id === playerId)?.nickname ?? '파도콩';
  }

  function updateConnection(): void {
    connectionBadge.textContent = connectionCopy(connection);
    connectionBadge.className = `party-connection ${connection === 'connected' ? '' : connection === 'offline' ? 'offline' : 'connecting'}`.trim();
  }

  function updateRoomMini(): void {
    roomMini.hidden = !room;
    roomMiniCode.textContent = room?.roomCode ?? '';
  }

  function updateUrl(roomCode: string | null): void {
    const url = new URL(window.location.href);
    if (roomCode) url.searchParams.set('room', roomCode);
    else url.searchParams.delete('room');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }

  function surfaceKey(): string {
    if (!room) {
      const rooms = publicRooms.map((candidate) => `${candidate.roomCode}:${candidate.playerCount}:${candidate.autoStartAt ?? ''}`).join('|');
      return `entry:${incomingRoom ?? ''}:${entryTab}:${publicRoomsLoaded}:${rooms}:${notice}:${connection}`;
    }
    if (room.phase === 'lobby') {
      const players = room.players.map((player) => `${player.id}:${player.ready}:${player.connected}:${player.isHost}`).join('|');
      return `lobby:${room.visibility}:${room.hostId}:${players}:${room.autoStartAt ?? ''}:${notice}`;
    }
    if (!currentView) return `invalid:${notice}`;
    if (currentView.mode === 'complete') return `complete:${currentView.phaseId}:${room.rematchReadyPlayerIds.join(',')}:${notice}`;
    return `arena:${currentView.matchId}`;
  }

  function renderSurface(force = false): void {
    const key = surfaceKey();
    if (!force && key === currentSurfaceKey) {
      updateRoomMini();
      updateGameHud();
      updateReconnectOverlay();
      return;
    }
    currentSurfaceKey = key;
    arenaCanvas = null;
    arenaContext = null;
    if (!room) {
      surface.innerHTML = partyEntryMarkup({
        incomingRoom,
        title: '코코넛 대포를 준비할까요?',
        description: '가입 없이 닉네임만 정하면 돼요. 2명부터 최대 4명까지 대전할 수 있어요.',
        notice,
        busy: connection === 'connecting' || connection === 'reconnecting',
        activeTab: entryTab,
        nicknameValue: entryNickname,
        publicRooms,
        publicRoomsLoaded,
      });
    } else if (room.phase === 'lobby') {
      surface.innerHTML = partyLobbyMarkup({
        room,
        selfId: network.playerId,
        gameLabel: '콩포트리스',
        startLabel: '포격전 시작!',
        notice,
      });
    } else if (!currentView) {
      surface.innerHTML = `
        <section class="party-panel center"><span class="party-eyebrow">새로고침이 필요해요</span><h2>경기 상태를 읽지 못했습니다</h2>
        <p>페이지를 새로고침해 같은 방으로 다시 연결해 주세요.</p><button class="party-primary" type="button" data-party-action="reload">새로고침</button></section>`;
    } else if (currentView.mode === 'complete') {
      renderComplete();
    } else {
      renderGame();
    }
    updateRoomMini();
    updateReconnectOverlay();
  }

  function playerCardMarkup(player: FortressPlayerView): string {
    const color = PARTY_PLAYER_COLORS[player.colorIndex % PARTY_PLAYER_COLORS.length];
    const connected = room?.players.find((candidate) => candidate.id === player.id)?.connected ?? false;
    const isCurrent = currentView?.turnPlayerId === player.id && currentView.mode === 'turn';
    const playerStatus = !player.alive
      ? '탈락'
      : !connected
        ? '재접속 중'
        : isCurrent
          ? '조준 중'
          : currentView?.turnPlayerId === player.id && currentView.mode === 'flight'
            ? '발사 완료'
            : '대기 중';
    return `
      <article class="fortress-player-card ${player.id === network.playerId ? 'self' : ''} ${isCurrent ? 'current' : ''} ${player.alive ? '' : 'eliminated'} ${connected ? '' : 'disconnected'}" style="--fortress-player-color:${color}">
        <span class="party-bean-avatar" style="--party-player-color:${color}" aria-hidden="true"><i></i></span>
        <span><strong>${escapePartyHtml(player.nickname)}</strong><small>${playerStatus}</small><span class="fortress-hp-track"><i style="width:${Math.max(0, player.health)}%"></i></span></span>
        <b>${player.health} HP</b>
      </article>`;
  }

  function renderGame(): void {
    if (!currentView) return;
    surface.innerHTML = `
      <section class="fortress-game-panel">
        <div class="fortress-turn-bar">
          <strong id="fortress-turn-number">TURN ${Math.max(1, currentView.turnNumber)}</strong>
          <span id="fortress-turn-status" class="fortress-turn-status">포격 준비 중</span>
          <b id="fortress-turn-timer">--</b>
        </div>
        <div id="fortress-scoreboard" class="fortress-scoreboard">${currentView.players.map(playerCardMarkup).join('')}</div>
        <div class="fortress-arena-frame">
          <div class="fortress-wind" role="status" aria-label="현재 바람">
            <span>바람</span><strong id="fortress-wind-value">${escapePartyHtml(windCopy(currentView.windMps))}</strong>
          </div>
          <canvas id="fortress-arena" width="${ARENA_WIDTH}" height="${ARENA_HEIGHT}" aria-label="콩포트리스 경기장"></canvas>
          <div id="fortress-overlay" class="fortress-overlay" role="status"></div>
        </div>
        <div class="fortress-controls" aria-label="포탄 조작">
          <section class="fortress-control-card fortress-move-card">
            <header><span>이동 · 방향 전환</span><output id="fortress-move-output">${currentView.movementRemaining} / ${FORTRESS_MOVE_PER_TURN}</output></header>
            <div class="fortress-control-row fortress-paired-buttons">
              <button type="button" data-fortress-move="-1" aria-label="왼쪽으로 이동"><b>←</b><small>A</small></button>
              <span class="fortress-move-track" aria-hidden="true"><i id="fortress-move-fill"></i></span>
              <button type="button" data-fortress-move="1" aria-label="오른쪽으로 이동"><b>→</b><small>D</small></button>
            </div>
          </section>
          <section class="fortress-control-card fortress-angle-card">
            <header><span>발사 각도</span><output id="fortress-angle-output">${aimFacing === 1 ? '→' : '←'} ${fortressAimElevation(angle)}°</output></header>
            <div class="fortress-control-row fortress-angle-buttons">
              <button type="button" data-fortress-angle="-1" aria-label="각도 낮추기"><b>↓</b><small>S</small></button>
              <span>위·아래로 조준</span>
              <button type="button" data-fortress-angle="1" aria-label="각도 높이기"><b>↑</b><small>W</small></button>
            </div>
          </section>
          <section class="fortress-control-card fortress-weapon-card">
            <header><span>포탄 선택</span><output id="fortress-weapon-output">${escapePartyHtml(fortressWeaponSpec(selectedWeapon).label)}</output></header>
            <div class="fortress-weapon-options" role="group" aria-label="사용할 포탄 선택">
              <button type="button" data-fortress-weapon="coconut" aria-pressed="${selectedWeapon === 'coconut'}">
                <i class="coconut" aria-hidden="true">🥥</i><span><b>기본 코코넛</b><small>넓은 폭발 · 최대 ${FORTRESS_WEAPONS.coconut.maximumDamage}</small></span><kbd>1</kbd>
              </button>
              <button type="button" data-fortress-weapon="precision-missile" aria-pressed="${selectedWeapon === 'precision-missile'}">
                <i class="missile" aria-hidden="true">➤</i><span><b>정밀 미사일</b><small>좁은 폭발 · 직격 ${FORTRESS_WEAPONS['precision-missile'].maximumDamage}</small></span><kbd>2</kbd>
              </button>
            </div>
          </section>
          <section class="fortress-power-card">
            <header><span>발사 파워</span><output id="fortress-power-output">${power}%</output></header>
            <div id="fortress-power-gauge" class="fortress-power-gauge" role="progressbar" aria-label="발사 파워" aria-valuemin="${FORTRESS_MIN_POWER}" aria-valuemax="${FORTRESS_MAX_POWER}" aria-valuenow="${power}"><i></i><b></b></div>
            <button id="fortress-fire" class="fortress-fire-button" type="button" data-fortress-charge><span id="fortress-fire-label">${weaponCopy(selectedWeapon)} 누르고 떼서 발사</span><small>SPACE</small></button>
          </section>
        </div>
        <p class="fortress-live-notice party-notice" role="status">${escapePartyHtml(notice)}</p>
        <p id="fortress-screen-reader-status" class="fortress-screen-reader-status" aria-live="polite"></p>
      </section>`;
    arenaCanvas = requireElement<HTMLCanvasElement>('#fortress-arena');
    arenaContext = arenaCanvas.getContext('2d');
    resizeArenaCanvas();
    updateGameHud();
  }

  function renderComplete(): void {
    if (!currentView || currentView.mode !== 'complete') return;
    const winner = currentView.players.find((player) => player.id === currentView?.winnerId) ?? null;
    const rematchReady = room?.rematchReadyPlayerIds.includes(network.playerId ?? '') ?? false;
    const ordered = [...currentView.players].sort((left, right) => right.health - left.health);
    surface.innerHTML = `
      <section class="fortress-result-panel">
        <span class="party-eyebrow">BATTLE COMPLETE</span>
        <div class="fortress-winner" style="--party-player-color:${PARTY_PLAYER_COLORS[(winner?.colorIndex ?? 2) % PARTY_PLAYER_COLORS.length]}"><span class="party-bean-avatar"><i></i></span><b>♛</b></div>
        <h2>${winner ? `${escapePartyHtml(winner.nickname)} 승리!` : '이번 포격전은 무승부!'}</h2>
        <p>${winner ? '파괴된 섬 위에서 마지막까지 살아남았어요.' : '남은 체력이 같거나 모두 함께 탈락했어요.'}</p>
        <div class="fortress-final-scores">
          ${ordered.map((player, index) => `<div><span>${index + 1}</span><strong>${escapePartyHtml(player.nickname)}</strong><b>${player.health} HP</b></div>`).join('')}
        </div>
        <div class="fortress-rematch-box">
          <strong>${room?.rematchReadyPlayerIds.length ?? 0}/${room?.players.filter((player) => player.connected).length ?? 0}명 재대결 준비</strong>
          <button class="party-primary full coral" type="button" data-party-action="rematch" ${rematchReady ? 'disabled' : ''}>${rematchReady ? '친구들을 기다리는 중' : '한 판 더!'}</button>
        </div>
        <button class="party-leave-button" type="button" data-party-action="leave">방 나가기</button>
        <p class="party-notice" role="status">${escapePartyHtml(notice)}</p>
        <p id="fortress-screen-reader-status" class="fortress-screen-reader-status" aria-live="polite">${winner ? `${escapePartyHtml(winner.nickname)} 승리` : '무승부'}</p>
      </section>`;
  }

  function canAim(): boolean {
    return Boolean(currentView
      && currentView.mode === 'turn'
      && currentView.turnPlayerId === network.playerId
      && ownGamePlayer()?.alive
      && connection === 'connected'
      && !firePending);
  }

  function canAdjustAim(): boolean {
    return canAim() && chargeStartedAt === null;
  }

  function syncAimControls(): void {
    const enabled = canAim();
    const adjustable = canAdjustAim();
    const angleOutput = surface.querySelector<HTMLOutputElement>('#fortress-angle-output');
    const powerOutput = surface.querySelector<HTMLOutputElement>('#fortress-power-output');
    const weaponOutput = surface.querySelector<HTMLOutputElement>('#fortress-weapon-output');
    const moveOutput = surface.querySelector<HTMLOutputElement>('#fortress-move-output');
    if (angleOutput) angleOutput.value = `${aimFacing === 1 ? '→' : '←'} ${fortressAimElevation(angle)}°`;
    if (powerOutput) powerOutput.value = `${power}%`;
    if (weaponOutput) weaponOutput.value = fortressWeaponSpec(selectedWeapon).label;
    if (moveOutput && currentView) {
      moveOutput.value = `${currentView.movementRemaining} / ${FORTRESS_MOVE_PER_TURN}`;
    }
    const moveFill = surface.querySelector<HTMLElement>('#fortress-move-fill');
    if (moveFill && currentView) {
      moveFill.style.width = `${currentView.movementRemaining / FORTRESS_MOVE_PER_TURN * 100}%`;
    }
    surface.querySelectorAll<HTMLButtonElement>('[data-fortress-move]')
      .forEach((button) => {
        button.disabled = !adjustable;
      });
    surface.querySelectorAll<HTMLButtonElement>('[data-fortress-angle]')
      .forEach((button) => { button.disabled = !adjustable; });
    surface.querySelectorAll<HTMLButtonElement>('[data-fortress-weapon]')
      .forEach((button) => {
        const isSelected = button.dataset.fortressWeapon === selectedWeapon;
        button.disabled = !adjustable;
        button.classList.toggle('selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });
    const gauge = surface.querySelector<HTMLElement>('#fortress-power-gauge');
    if (gauge) {
      const percentage = (power - FORTRESS_MIN_POWER) / (FORTRESS_MAX_POWER - FORTRESS_MIN_POWER) * 100;
      gauge.style.setProperty('--fortress-power-percent', `${percentage}%`);
      gauge.setAttribute('aria-valuenow', String(power));
      gauge.classList.toggle('charging', chargeStartedAt !== null);
    }
    const chargeButton = surface.querySelector<HTMLButtonElement>('[data-fortress-charge]');
    if (chargeButton) {
      chargeButton.disabled = !enabled;
      chargeButton.classList.toggle('charging', chargeStartedAt !== null);
      const fireLabel = chargeButton.querySelector<HTMLElement>('#fortress-fire-label');
      if (fireLabel) fireLabel.textContent = chargeStartedAt === null
        ? `${weaponCopy(selectedWeapon)} 누르고 떼서 발사`
        : `지금 떼면 ${weaponCopy(selectedWeapon)} 발사!`;
    }
  }

  function updateGameHud(): void {
    if (room?.phase === 'lobby') {
      updatePartyLobbyAutoStart(surface, room, Date.now() + serverClockDelta);
      return;
    }
    if (!currentView || currentView.mode === 'complete') return;
    const now = Date.now() + serverClockDelta;
    const remaining = Math.max(0, currentView.deadlineAt - now);
    const turnNumber = surface.querySelector<HTMLElement>('#fortress-turn-number');
    const timer = surface.querySelector<HTMLElement>('#fortress-turn-timer');
    const status = surface.querySelector<HTMLElement>('#fortress-turn-status');
    const wind = surface.querySelector<HTMLElement>('#fortress-wind-value');
    const scoreboard = surface.querySelector<HTMLDivElement>('#fortress-scoreboard');
    const overlay = surface.querySelector<HTMLDivElement>('#fortress-overlay');
    if (turnNumber) turnNumber.textContent = `TURN ${Math.max(1, currentView.turnNumber)}`;
    if (timer) timer.textContent = currentView.mode === 'turn' ? `${Math.ceil(remaining / 1_000)}초` : currentView.mode === 'flight' ? '비행 중' : '--';
    if (status) {
      const main = currentView.mode === 'countdown'
        ? '포격 준비 중'
        : currentView.mode === 'turn'
          ? currentView.turnPlayerId === network.playerId ? '내 차례 · 조준하세요!' : `${playerName(currentView.turnPlayerId)} 조준 중`
          : currentView.mode === 'flight'
            ? `${weaponCopy(currentView.projectile?.weapon ?? 'coconut')} 비행 중`
            : '폭발 결과 확인 중';
      status.innerHTML = `${escapePartyHtml(main)}${currentView.suddenDeath ? '<em>최종전</em>' : ''}`;
    }
    if (wind && wind.textContent !== windCopy(currentView.windMps)) wind.textContent = windCopy(currentView.windMps);
    if (scoreboard) scoreboard.innerHTML = currentView.players.map(playerCardMarkup).join('');
    if (overlay) {
      if (currentView.mode === 'countdown') {
        overlay.className = 'fortress-overlay visible';
        overlay.innerHTML = `<small>MAP · ${escapePartyHtml(fortressMapSpec(currentView.mapId).label)}</small><strong>${Math.max(1, Math.ceil(remaining / 800))}</strong><span>랜덤 자리에서 포격 준비!</span>`;
      } else if (currentView.mode === 'impact') {
        const damage = currentView.explosion?.damage ?? [];
        const damageCopy = damage.length > 0
          ? damage.map((entry) => `${escapePartyHtml(playerName(entry.playerId))} -${entry.amount}`).join(' · ')
          : currentView.explosion ? '지형이 크게 파였어요!' : '포탄이 섬을 벗어났어요!';
        const impactCopy = currentView.explosion?.directHitPlayerId
          ? 'DIRECT HIT!'
          : currentView.explosion ? 'BOOM!' : 'MISS';
        overlay.className = 'fortress-overlay visible impact';
        overlay.innerHTML = `<small>${impactCopy}</small><strong>${damageCopy}</strong><span>${Math.ceil(remaining / 1_000)}초 뒤 다음 턴</span>`;
      } else {
        overlay.className = 'fortress-overlay';
        overlay.innerHTML = '';
      }
    }
    const canvas = arenaCanvas;
    if (canvas) {
      const alive = currentView.players.filter((player) => player.alive).length;
      canvas.setAttribute('aria-label', `콩포트리스 ${fortressMapSpec(currentView.mapId).label} 경기장, ${playerName(currentView.turnPlayerId)} 차례, 생존 ${alive}명, ${windCopy(currentView.windMps)}`);
    }
    const noticeElement = surface.querySelector<HTMLElement>('.fortress-live-notice');
    if (noticeElement) noticeElement.textContent = notice;
    syncAimControls();
  }

  function updateReconnectOverlay(): void {
    surface.querySelector('.fortress-reconnect-overlay')?.remove();
    if (!room || connection === 'connected') return;
    const overlay = document.createElement('div');
    overlay.className = 'fortress-reconnect-overlay';
    overlay.innerHTML = `<strong>${connection === 'reconnecting' ? '재접속하는 중…' : '연결이 끊겼어요'}</strong><span>턴과 파괴된 지형은 서버에 남아 있어요. 연결되면 현재 전장으로 돌아옵니다.</span>${connection === 'offline' ? '<div><button type="button" data-party-action="retry-connection">다시 연결</button><button type="button" data-party-action="leave">방 나가기</button></div>' : ''}`;
    surface.append(overlay);
  }

  function resizeArenaCanvas(): void {
    if (!arenaCanvas || !arenaContext) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    arenaCanvas.width = Math.round(ARENA_WIDTH * dpr);
    arenaCanvas.height = Math.round(ARENA_HEIGHT * dpr);
    arenaContext.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function drawCloud(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    scale: number,
    color: string,
  ): void {
    context.save();
    context.translate(x, y);
    context.scale(scale, scale);
    context.fillStyle = color;
    context.beginPath();
    context.arc(-24, 3, 18, 0, Math.PI * 2);
    context.arc(0, -5, 25, 0, Math.PI * 2);
    context.arc(28, 4, 17, 0, Math.PI * 2);
    context.fill();
    context.restore();
  }

  function drawBean(
    context: CanvasRenderingContext2D,
    player: FortressPlayerView,
    x: number,
    y: number,
    scaleX: number,
    scaleY: number,
  ): void {
    const color = PARTY_PLAYER_COLORS[player.colorIndex % PARTY_PLAYER_COLORS.length];
    const bodySize = Math.max(46, Math.min(60, (scaleX + scaleY) * 3.2));
    const defaultAngle = player.x < (currentView?.terrain.length ?? FORTRESS_TERRAIN_WIDTH) / 2 ? 45 : 135;
    const cannonAngle = currentView?.turnPlayerId === player.id && player.id === network.playerId ? angle : defaultAngle;
    const radians = -cannonAngle * Math.PI / 180;
    const facing = currentView?.turnPlayerId === player.id && player.id === network.playerId
      ? aimFacing : Math.cos(radians) >= 0 ? 1 : -1;
    const pivotX = facing * bodySize * .14;
    const pivotY = -bodySize * .24;
    const isLocalAim = player.alive
      && currentView?.mode === 'turn'
      && currentView.turnPlayerId === player.id
      && player.id === network.playerId;
    context.save();
    context.translate(x, y - bodySize * .2);
    context.globalAlpha = player.alive ? 1 : .45;

    if (isLocalAim) {
      const baseRadians = facing > 0 ? 0 : -Math.PI;
      const guideRadius = bodySize * 1.18;
      const guideColor = selectedWeapon === 'precision-missile' ? '#ff7359' : '#fff1a8';
      context.save();
      context.translate(pivotX, pivotY);
      context.fillStyle = selectedWeapon === 'precision-missile'
        ? 'rgba(255,115,89,.14)'
        : 'rgba(255,241,168,.18)';
      context.beginPath();
      context.moveTo(0, 0);
      for (let step = 0; step <= 18; step += 1) {
        const progress = step / 18;
        const guideAngle = baseRadians + (radians - baseRadians) * progress;
        context.lineTo(Math.cos(guideAngle) * guideRadius, Math.sin(guideAngle) * guideRadius);
      }
      context.closePath();
      context.fill();
      context.strokeStyle = guideColor;
      context.lineWidth = 4;
      context.lineCap = 'round';
      context.beginPath();
      for (let step = 0; step <= 18; step += 1) {
        const progress = step / 18;
        const guideAngle = baseRadians + (radians - baseRadians) * progress;
        const guideX = Math.cos(guideAngle) * guideRadius;
        const guideY = Math.sin(guideAngle) * guideRadius;
        if (step === 0) context.moveTo(guideX, guideY);
        else context.lineTo(guideX, guideY);
      }
      context.stroke();
      context.setLineDash([7, 6]);
      context.lineWidth = 3;
      context.beginPath();
      context.moveTo(Math.cos(radians) * bodySize * .68, Math.sin(radians) * bodySize * .68);
      context.lineTo(Math.cos(radians) * bodySize * 1.7, Math.sin(radians) * bodySize * 1.7);
      context.stroke();
      context.setLineDash([]);
      context.fillStyle = guideColor;
      context.beginPath();
      context.arc(Math.cos(radians) * bodySize * 1.7, Math.sin(radians) * bodySize * 1.7, 4.5, 0, Math.PI * 2);
      context.fill();
      context.restore();
    }

    context.fillStyle = 'rgba(23,35,56,.18)';
    context.beginPath();
    context.ellipse(0, bodySize * .48, bodySize * .59, bodySize * .15, 0, 0, Math.PI * 2);
    context.fill();

    context.fillStyle = color;
    context.strokeStyle = '#172338';
    context.lineWidth = 4;
    context.beginPath();
    context.ellipse(-bodySize * .2, bodySize * .35, bodySize * .21, bodySize * .13, -.18, 0, Math.PI * 2);
    context.ellipse(bodySize * .2, bodySize * .35, bodySize * .21, bodySize * .13, .18, 0, Math.PI * 2);
    context.fill();
    context.stroke();

    context.fillStyle = color;
    context.strokeStyle = '#172338';
    context.beginPath();
    context.moveTo(-bodySize * .09, -bodySize * .51);
    context.bezierCurveTo(-bodySize * .4, -bodySize * .48, -bodySize * .54, -bodySize * .2, -bodySize * .5, bodySize * .08);
    context.bezierCurveTo(-bodySize * .47, bodySize * .35, -bodySize * .24, bodySize * .47, bodySize * .06, bodySize * .43);
    context.bezierCurveTo(bodySize * .39, bodySize * .4, bodySize * .52, bodySize * .17, bodySize * .47, -bodySize * .08);
    context.bezierCurveTo(bodySize * .43, -bodySize * .3, bodySize * .25, -bodySize * .42, bodySize * .05, -bodySize * .43);
    context.bezierCurveTo(bodySize * .04, -bodySize * .5, 0, -bodySize * .55, -bodySize * .09, -bodySize * .51);
    context.closePath();
    context.fill();
    context.stroke();

    context.strokeStyle = 'rgba(255,255,255,.6)';
    context.lineWidth = 4;
    context.lineCap = 'round';
    context.beginPath();
    context.moveTo(-bodySize * .27, -bodySize * .28);
    context.quadraticCurveTo(-bodySize * .36, -bodySize * .08, -bodySize * .29, bodySize * .08);
    context.stroke();

    context.strokeStyle = '#172338';
    context.lineWidth = 3.5;
    context.beginPath();
    context.moveTo(facing * bodySize * .36, bodySize * .02);
    context.quadraticCurveTo(facing * bodySize * .57, bodySize * .08, facing * bodySize * .6, -bodySize * .08);
    context.stroke();

    const faceX = facing * bodySize * .12;
    context.fillStyle = '#172338';
    context.beginPath();
    context.ellipse(faceX - bodySize * .1, -bodySize * .1, bodySize * .052, bodySize * .073, 0, 0, Math.PI * 2);
    context.ellipse(faceX + bodySize * .09, -bodySize * .09, bodySize * .052, bodySize * .073, 0, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = 'rgba(255,116,112,.72)';
    context.beginPath();
    context.ellipse(faceX + facing * bodySize * .18, bodySize * .02, bodySize * .09, bodySize * .052, 0, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = '#172338';
    context.lineWidth = 2.2;
    context.beginPath();
    context.arc(faceX, bodySize * .02, bodySize * .09, .18 * Math.PI, .82 * Math.PI);
    context.stroke();

    if (player.alive) {
      context.save();
      context.translate(pivotX, pivotY);
      context.rotate(radians);
      const barrelLength = bodySize * .78;
      const barrel = context.createLinearGradient(0, -6, 0, 6);
      barrel.addColorStop(0, selectedWeapon === 'precision-missile' && isLocalAim ? '#ff8b72' : '#c77b43');
      barrel.addColorStop(1, '#744326');
      context.fillStyle = barrel;
      context.strokeStyle = '#172338';
      context.lineWidth = 4;
      context.beginPath();
      context.moveTo(-2, -7);
      context.lineTo(barrelLength, -5);
      context.lineTo(barrelLength, 5);
      context.lineTo(-2, 7);
      context.closePath();
      context.fill();
      context.stroke();
      context.fillStyle = selectedWeapon === 'precision-missile' && isLocalAim ? '#f8f5e8' : '#5b3523';
      context.beginPath();
      context.ellipse(barrelLength, 0, 4, 7, 0, 0, Math.PI * 2);
      context.fill();
      context.stroke();
      context.restore();
      context.fillStyle = '#9a5b31';
      context.strokeStyle = '#172338';
      context.lineWidth = 4;
      context.beginPath();
      context.arc(pivotX, pivotY, bodySize * .17, 0, Math.PI * 2);
      context.fill();
      context.stroke();
      context.fillStyle = color;
      context.beginPath();
      context.arc(pivotX, pivotY, bodySize * .08, 0, Math.PI * 2);
      context.fill();
    } else {
      context.strokeStyle = '#fff';
      context.lineWidth = 2.5;
      context.beginPath();
      context.moveTo(faceX - 8, -9); context.lineTo(faceX - 2, -3);
      context.moveTo(faceX - 2, -9); context.lineTo(faceX - 8, -3);
      context.moveTo(faceX + 2, -9); context.lineTo(faceX + 8, -3);
      context.moveTo(faceX + 8, -9); context.lineTo(faceX + 2, -3);
      context.stroke();
    }
    context.restore();
  }

  function drawArena(now: number): void {
    if (!arenaContext || !currentView || currentView.mode === 'complete') return;
    const context = arenaContext;
    const mapTheme = FORTRESS_MAP_THEMES[currentView.mapId];
    const projection = fortressArenaProjection(currentView.terrain.length, ARENA_WIDTH, ARENA_HEIGHT);
    const scaleX = projection.scale;
    const scaleY = projection.scale;
    const offsetY = projection.offsetY;
    const sky = context.createLinearGradient(0, 0, 0, ARENA_HEIGHT);
    sky.addColorStop(0, mapTheme.sky[0]);
    sky.addColorStop(.72, mapTheme.sky[1]);
    sky.addColorStop(1, mapTheme.sky[2]);
    context.fillStyle = sky;
    context.fillRect(0, 0, ARENA_WIDTH, ARENA_HEIGHT);
    context.fillStyle = mapTheme.sun;
    context.beginPath();
    context.arc(ARENA_WIDTH * .82, ARENA_HEIGHT * .17, 42, 0, Math.PI * 2);
    context.fill();
    drawCloud(context, 140, 92, 1.05, mapTheme.cloud);
    drawCloud(context, 530, 118, .72, mapTheme.cloud);
    drawCloud(context, 820, 190, .52, mapTheme.cloud);
    context.fillStyle = mapTheme.backdrop;
    context.beginPath();
    context.moveTo(0, ARENA_HEIGHT * .65);
    for (let x = 0; x <= ARENA_WIDTH; x += 80) {
      context.lineTo(x, ARENA_HEIGHT * .65 - Math.sin(x * .018) * 18 - (x % 160 === 0 ? 15 : 0));
    }
    context.lineTo(ARENA_WIDTH, ARENA_HEIGHT);
    context.lineTo(0, ARENA_HEIGHT);
    context.fill();

    context.beginPath();
    context.moveTo(0, offsetY + currentView.terrain[0] * scaleY);
    currentView.terrain.forEach((height, x) => context.lineTo(x * scaleX, offsetY + height * scaleY));
    context.lineTo(ARENA_WIDTH, ARENA_HEIGHT);
    context.lineTo(0, ARENA_HEIGHT);
    context.closePath();
    const earth = context.createLinearGradient(0, ARENA_HEIGHT * .48, 0, ARENA_HEIGHT);
    earth.addColorStop(0, mapTheme.earth[0]);
    earth.addColorStop(1, mapTheme.earth[1]);
    context.fillStyle = earth;
    context.fill();
    context.strokeStyle = mapTheme.ridge;
    context.lineWidth = 5;
    context.beginPath();
    currentView.terrain.forEach((height, x) => x === 0
      ? context.moveTo(0, offsetY + height * scaleY)
      : context.lineTo(x * scaleX, offsetY + height * scaleY));
    context.stroke();
    context.strokeStyle = mapTheme.grass;
    context.lineWidth = 8;
    context.stroke();

    context.save();
    context.fillStyle = mapTheme.badge;
    context.beginPath();
    context.roundRect(22, 20, 172, 46, 14);
    context.fill();
    context.fillStyle = mapTheme.badgeText;
    context.font = '700 20px Pretendard, system-ui, sans-serif';
    context.textBaseline = 'middle';
    context.fillText(`MAP · ${fortressMapSpec(currentView.mapId).label}`, 39, 43);
    context.restore();

    for (const player of currentView.players) {
      const position = renderedPlayerPosition(player, now);
      drawBean(context, player, position.x * scaleX, offsetY + position.y * scaleY, scaleX, scaleY);
    }

    if (currentView.projectile) {
      const interpolation = Math.min(1, Math.max(0, (now - projectileSnapshotAt) / 50));
      const start = previousProjectile ?? currentView.projectile;
      const x = mix(start.x, currentView.projectile.x, interpolation);
      const y = mix(start.y, currentView.projectile.y, interpolation);
      renderedProjectile = { x, y };
      context.save();
      context.translate(x * scaleX, offsetY + y * scaleY);
      if (currentView.projectile.weapon === 'precision-missile') {
        context.rotate(Math.atan2(currentView.projectile.vy * scaleY, currentView.projectile.vx * scaleX));
        context.shadowColor = 'rgba(255,120,80,.78)';
        context.shadowBlur = 12;
        context.fillStyle = '#ffb34d';
        context.beginPath();
        context.moveTo(-13, 0);
        context.lineTo(-20, -5);
        context.lineTo(-18, 0);
        context.lineTo(-20, 5);
        context.closePath();
        context.fill();
        context.fillStyle = '#f4f7f2';
        context.strokeStyle = '#172338';
        context.lineWidth = 2.5;
        context.beginPath();
        context.moveTo(-12, -5);
        context.lineTo(7, -5);
        context.quadraticCurveTo(14, 0, 7, 5);
        context.lineTo(-12, 5);
        context.closePath();
        context.fill();
        context.stroke();
        context.fillStyle = '#ff7359';
        context.fillRect(-7, -4, 7, 8);
        context.beginPath();
        context.moveTo(-9, -5); context.lineTo(-14, -10); context.lineTo(-2, -5);
        context.moveTo(-9, 5); context.lineTo(-14, 10); context.lineTo(-2, 5);
        context.fill();
      } else {
        context.fillStyle = '#704322';
        context.strokeStyle = '#172338';
        context.lineWidth = 3;
        context.shadowColor = 'rgba(255,255,255,.7)';
        context.shadowBlur = 10;
        context.beginPath();
        context.arc(0, 0, 8, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.strokeStyle = '#caa071';
        context.lineWidth = 1.5;
        context.beginPath();
        context.moveTo(-4, -5); context.lineTo(4, 5);
        context.stroke();
      }
      context.restore();
    } else {
      renderedProjectile = null;
    }

    if (currentView.mode === 'impact' && currentView.explosion) {
      const remaining = Math.max(0, currentView.deadlineAt - (Date.now() + serverClockDelta));
      const progress = 1 - remaining / 1_700;
      const pulse = Math.sin(Math.min(1, Math.max(0, progress)) * Math.PI);
      const x = currentView.explosion.x * scaleX;
      const y = offsetY + currentView.explosion.y * scaleY;
      const explosionRings = currentView.explosion.weapon === 'precision-missile'
        ? [[38, 'rgba(255,80,62,.55)'], [25, 'rgba(255,190,60,.82)'], [12, '#fffde1']] as const
        : [[54, 'rgba(255,116,70,.48)'], [37, 'rgba(255,211,78,.76)'], [20, '#fff7ca']] as const;
      for (const [radius, color] of explosionRings) {
        context.fillStyle = color;
        context.beginPath();
        context.arc(x, y, radius * (.45 + pulse), 0, Math.PI * 2);
        context.fill();
      }
      if (currentView.explosion.weapon === 'precision-missile') {
        context.save();
        context.translate(x, y);
        context.rotate(progress * .6);
        context.fillStyle = `rgba(255,247,202,${.35 + pulse * .55})`;
        context.beginPath();
        for (let point = 0; point < 16; point += 1) {
          const pointAngle = point / 16 * Math.PI * 2;
          const pointRadius = (point % 2 === 0 ? 48 : 17) * (.45 + pulse);
          const pointX = Math.cos(pointAngle) * pointRadius;
          const pointY = Math.sin(pointAngle) * pointRadius;
          if (point === 0) context.moveTo(pointX, pointY);
          else context.lineTo(pointX, pointY);
        }
        context.closePath();
        context.fill();
        context.restore();
      }
    }
  }

  function announcePhase(view: MaterializedFortressView): void {
    if (view.phaseId === announcedPhaseId) return;
    announcedPhaseId = view.phaseId;
    const live = surface.querySelector<HTMLElement>('#fortress-screen-reader-status');
    if (!live) return;
    if (view.mode === 'turn') live.textContent = view.turnPlayerId === network.playerId ? '내 차례입니다. 각도와 세기를 정해 발사하세요.' : `${playerName(view.turnPlayerId)} 차례입니다.`;
    else if (view.mode === 'impact') live.textContent = view.explosion?.damage.length
      ? view.explosion.damage.map((entry) => `${playerName(entry.playerId)} ${entry.amount} 피해`).join(', ')
      : view.explosion ? '포탄이 지형에 명중했습니다.' : '포탄이 빗나갔습니다.';
    else if (view.mode === 'complete') live.textContent = view.winnerId ? `${playerName(view.winnerId)} 승리` : '무승부';
  }

  function handleRoomUpdate(nextRoom: PartyRoomView): void {
    const rawView = isFortressView(nextRoom.game) ? nextRoom.game : null;
    let terrain: readonly number[] | null = rawView?.terrain ?? null;
    if (rawView && !terrain && currentView?.matchId === rawView.matchId) {
      if (rawView.resetTerrain) terrain = null;
      else if (rawView.terrainVersion === currentView.terrainVersion) terrain = currentView.terrain;
      else if (rawView.terrainVersion === currentView.terrainVersion + 1) {
        terrain = applyFortressTerrainPatch(currentView.terrain, rawView.terrainPatch);
      }
    }
    const nextView: MaterializedFortressView | null = rawView && terrain
      ? { ...rawView, terrain }
      : null;
    const snapshotAt = performance.now();
    if (nextView?.mode === 'turn' && currentView?.mode === 'turn'
      && nextView.matchId === currentView.matchId && nextView.turnId === currentView.turnId
      && nextView.terrainVersion === currentView.terrainVersion) {
      for (const player of nextView.players) {
        const previous = currentView.players.find((candidate) => candidate.id === player.id);
        if (previous && (previous.x !== player.x || previous.y !== player.y)) {
          playerMovements.set(player.id, { from: renderedPlayerPosition(previous, snapshotAt), startedAt: snapshotAt });
        }
      }
    } else {
      playerMovements.clear();
    }
    if (nextView?.projectile) {
      const currentProjectile = currentView?.projectile;
      previousProjectile = renderedProjectile
        ?? (currentProjectile?.ownerId === nextView.projectile.ownerId
          ? { x: currentProjectile.x, y: currentProjectile.y }
          : { x: nextView.projectile.x, y: nextView.projectile.y });
      projectileSnapshotAt = performance.now();
    } else {
      previousProjectile = null;
      renderedProjectile = null;
    }
    if (nextView && nextView.turnId !== aimTurnId) {
      aimTurnId = nextView.turnId;
      const self = nextView.players.find((player) => player.id === network.playerId);
      aimFacing = self && self.x >= nextView.terrain.length / 2 ? -1 : 1;
      angle = fortressAngleFacing(45, aimFacing);
      power = FORTRESS_MIN_POWER;
      selectedWeapon = 'coconut';
      firePending = false;
      chargeStartedAt = null;
      chargePointerId = null;
      movementHold = null;
      angleHold = null;
      controlPointerId = null;
    }
    movementPending = false;
    if (!nextView || nextView.mode !== 'turn' || nextView.turnPlayerId !== network.playerId) {
      chargeStartedAt = null;
      chargePointerId = null;
      movementHold = null;
      angleHold = null;
      controlPointerId = null;
    }
    if (nextView?.mode !== 'flight') firePending = false;
    room = nextRoom;
    currentView = nextView;
    serverClockDelta = nextRoom.serverTime - Date.now();
    notice = '';
    updateUrl(nextRoom.roomCode);
    renderSurface();
    if (nextView) announcePhase(nextView);
  }

  function handleMessage(message: PartyServerMessage): void {
    if (message.type === 'room' || message.type === 'state') {
      handleRoomUpdate(message.room);
    } else if (message.type === 'public-rooms' && message.gameId === 'kong-fortress') {
      publicRooms = message.rooms;
      publicRoomsLoaded = true;
      renderSurface();
    } else if (message.type === 'error') {
      notice = message.message;
      firePending = false;
      movementPending = false;
      movementHold = null;
      if (room?.phase === 'playing') updateGameHud();
      else renderSurface(true);
    } else if (message.type === 'closed') {
      room = null;
      currentView = null;
      notice = closedReasonCopy(message.reason);
      updateUrl(null);
      renderSurface(true);
      if (entryTab === 'public') window.setTimeout(() => network.requestPublicRooms(), 150);
    }
  }

  function inviteUrl(): string {
    return partyInviteUrl(GAME_PATH, room?.roomCode ?? '');
  }

  async function copyInvite(): Promise<void> {
    if (!room) return;
    notice = await copyPartyText(inviteUrl()) ? '초대 링크를 복사했어요.' : '링크를 복사하지 못했습니다.';
    renderSurface(true);
  }

  async function shareInvite(): Promise<void> {
    if (!room) return;
    const mode = await sharePartyInvite(GAME_TITLE, `${room.roomCode} 방에서 같이 콩포트리스 해요!`, inviteUrl());
    notice = kakaoCardShareMessage(mode);
    renderSurface(true);
  }

  function renderedPlayerPosition(player: FortressPlayerView, now: number): { x: number; y: number } {
    const movement = playerMovements.get(player.id);
    return movement ? fortressMovementPosition(movement.from, player, now - movement.startedAt) : player;
  }

  function adjustAngle(direction: FortressDirection): void {
    if (!canAdjustAim()) return;
    angle = fortressAdjustAngle(angle, direction, aimFacing);
    syncAimControls();
  }

  function selectWeapon(weapon: FortressWeaponId): void {
    if (!canAdjustAim() || selectedWeapon === weapon) return;
    selectedWeapon = weapon;
    syncAimControls();
  }

  function move(direction: -1 | 1): boolean {
    if (!currentView || !canAdjustAim()) return false;
    aimFacing = direction;
    angle = fortressAngleFacing(angle, aimFacing);
    syncAimControls();
    if (movementPending || currentView.movementRemaining <= 0) return false;
    movementPending = true;
    syncAimControls();
    network.action({ type: 'move', turnId: currentView.turnId, direction });
    return true;
  }

  function updateChargedPower(now: number): void {
    if (chargeStartedAt === null) return;
    const nextPower = fortressPowerAtElapsed(now - chargeStartedAt);
    if (nextPower === power) return;
    power = nextPower;
    syncAimControls();
  }

  function startCharge(pointerId: number | null = null): void {
    if (!canAim() || chargeStartedAt !== null) return;
    movementHold = null;
    angleHold = null;
    controlPointerId = null;
    power = FORTRESS_MIN_POWER;
    chargeStartedAt = performance.now();
    chargePointerId = pointerId;
    syncAimControls();
  }

  function releaseCharge(pointerId: number | null = null): void {
    if (chargeStartedAt === null || (chargePointerId !== null && pointerId !== chargePointerId)) return;
    updateChargedPower(performance.now());
    chargeStartedAt = null;
    chargePointerId = null;
    fire();
  }

  function cancelActiveInputs(): void {
    movementHold = null;
    angleHold = null;
    controlPointerId = null;
    chargeStartedAt = null;
    chargePointerId = null;
    power = FORTRESS_MIN_POWER;
    syncAimControls();
  }

  function beginMovementHold(direction: -1 | 1, now = performance.now()): void {
    if (!canAdjustAim() || movementHold?.direction === direction) return;
    movementHold = { direction, nextAt: now + FORTRESS_MOVE_REPEAT_MILLISECONDS };
    move(direction);
  }

  function beginAngleHold(direction: -1 | 1, now = performance.now()): void {
    if (!canAdjustAim() || angleHold?.direction === direction) return;
    angleHold = { direction, nextAt: now + 280 };
    adjustAngle(direction);
  }

  function processHeldControls(now: number): void {
    if (movementHold && now >= movementHold.nextAt) {
      if (move(movementHold.direction)) movementHold.nextAt = now + FORTRESS_MOVE_REPEAT_MILLISECONDS;
      else if (!canAdjustAim() || (currentView?.movementRemaining ?? 0) <= 0) movementHold = null;
    }
    if (angleHold && now >= angleHold.nextAt) {
      adjustAngle(angleHold.direction);
      angleHold.nextAt = now + 70;
    }
  }

  function fire(): void {
    if (!currentView || !canAim()) return;
    firePending = true;
    syncAimControls();
    network.action({ type: 'fire', turnId: currentView.turnId, angle, power, weapon: selectedWeapon });
  }

  surface.addEventListener('submit', (event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    if (form.id !== 'party-entry-form') return;
    const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null;
    const nickname = requireElement<HTMLInputElement>('#party-nickname').value.trim();
    const mode = submitter?.dataset.entryMode ?? 'create-private';
    if (!nickname) {
      notice = '닉네임을 입력해 주세요.';
      renderSurface(true);
      return;
    }
    savePartyNickname(nickname);
    entryNickname = nickname;
    notice = '';
    if (mode === 'join-private') {
      const roomCode = normalizePartyRoomCode(requireElement<HTMLInputElement>('#party-room-code').value);
      if (roomCode.length !== 6) {
        notice = '6자리 방 코드를 입력해 주세요.';
        renderSurface(true);
        return;
      }
      network.joinRoom(roomCode, nickname);
    } else if (mode === 'join-public') {
      const roomCode = normalizePartyRoomCode(submitter?.dataset.roomCode ?? '');
      if (roomCode.length !== 6) return;
      network.joinRoom(roomCode, nickname);
    } else if (mode === 'quick-public') {
      network.quickJoin(nickname);
    } else if (mode === 'create-public') {
      network.createRoom(nickname, 'public');
    } else {
      network.createRoom(nickname, 'private');
    }
  });

  surface.addEventListener('input', (event) => {
    const input = event.target as HTMLInputElement;
    if (input.id === 'party-nickname') entryNickname = input.value;
    else if (input.id === 'party-room-code') input.value = normalizePartyRoomCode(input.value);
  });

  surface.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-party-action], [data-party-entry-tab], [data-fortress-move], [data-fortress-angle], [data-fortress-weapon], [data-fortress-charge]');
    if (!target) return;
    const nextEntryTab = target.dataset.partyEntryTab as PartyEntryTab | undefined;
    if (nextEntryTab) {
      entryTab = nextEntryTab;
      notice = '';
      renderSurface(true);
      if (entryTab === 'public') network.requestPublicRooms();
      return;
    }
    if (target.dataset.fortressMove) {
      if (event.detail === 0) move(Number(target.dataset.fortressMove) as -1 | 1);
      return;
    }
    if (target.dataset.fortressAngle) {
      if (event.detail === 0) adjustAngle(Number(target.dataset.fortressAngle) as FortressDirection);
      return;
    }
    if (target.dataset.fortressWeapon) {
      selectWeapon(target.dataset.fortressWeapon as FortressWeaponId);
      return;
    }
    if (target.hasAttribute('data-fortress-charge')) {
      event.preventDefault();
      return;
    }
    const action = target.dataset.partyAction;
    if (action === 'refresh-public') {
      notice = '';
      publicRoomsLoaded = false;
      renderSurface(true);
      network.requestPublicRooms();
    } else if (action === 'copy-invite') void copyInvite();
    else if (action === 'share-invite') void shareInvite();
    else if (action === 'ready') network.setReady(!(ownRoomPlayer()?.ready ?? false));
    else if (action === 'start') network.start();
    else if (action === 'retry-connection') network.reconnect();
    else if (action === 'leave') {
      if (room?.phase === 'playing' && !window.confirm('진행 중인 콩포트리스에서 나갈까요?')) return;
      network.leave();
      room = null;
      currentView = null;
      notice = '방에서 나왔습니다.';
      updateUrl(null);
      renderSurface(true);
      if (entryTab === 'public') window.setTimeout(() => network.requestPublicRooms(), 50);
    } else if (action === 'rematch') network.rematch();
    else if (action === 'reload') window.location.reload();
  });

  surface.addEventListener('pointerdown', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-fortress-charge], [data-fortress-move], [data-fortress-angle]');
    if (!target || target.disabled || event.button !== 0 || !event.isPrimary) return;
    event.preventDefault();
    target.setPointerCapture(event.pointerId);
    if (target.hasAttribute('data-fortress-charge')) startCharge(event.pointerId);
    else {
      controlPointerId = event.pointerId;
      if (target.dataset.fortressMove) beginMovementHold(Number(target.dataset.fortressMove) as FortressDirection);
      else beginAngleHold(Number(target.dataset.fortressAngle) as FortressDirection);
    }
  });

  surface.addEventListener('pointerup', (event) => {
    if (controlPointerId === event.pointerId) {
      event.preventDefault();
      controlPointerId = null;
      movementHold = null;
      angleHold = null;
    } else if (chargePointerId === event.pointerId) {
      event.preventDefault();
      releaseCharge(event.pointerId);
    }
  });

  function cancelPointerInput(event: PointerEvent): void {
    if (chargePointerId !== event.pointerId && controlPointerId !== event.pointerId) return;
    cancelActiveInputs();
  }
  surface.addEventListener('pointercancel', cancelPointerInput);
  surface.addEventListener('lostpointercapture', cancelPointerInput);

  document.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-party-action="copy-code"]');
    if (target && room) void copyPartyText(room.roomCode);
  });

  window.addEventListener('keydown', (event) => {
    const target = event.target as HTMLElement;
    if (event.ctrlKey || event.metaKey || event.altKey || !canAim()) return;
    if (target.isContentEditable || target.closest('input, textarea, select')) return;
    if (target.closest('button, a, [role="button"]') && !target.closest('.fortress-controls')) return;
    const control = fortressKeyboardControl(event);
    if (!control) return;
    event.preventDefault();
    if (event.repeat) return;
    if (control === 'move-left') beginMovementHold(-1);
    else if (control === 'move-right') beginMovementHold(1);
    else if (control === 'aim-up') beginAngleHold(1);
    else if (control === 'aim-down') beginAngleHold(-1);
    else if (control === 'coconut' || control === 'precision-missile') selectWeapon(control);
    else startCharge();
  });
  window.addEventListener('keyup', (event) => {
    // Release active inputs even if focus changed while a key was held.
    const control = fortressKeyboardControl(event);
    if (control === 'move-left' && movementHold?.direction === -1) {
      event.preventDefault(); movementHold = null;
    } else if (control === 'move-right' && movementHold?.direction === 1) {
      event.preventDefault(); movementHold = null;
    } else if (control === 'aim-up' && angleHold?.direction === 1) {
      event.preventDefault(); angleHold = null;
    } else if (control === 'aim-down' && angleHold?.direction === -1) {
      event.preventDefault(); angleHold = null;
    } else if (control === 'charge' && chargeStartedAt !== null && chargePointerId === null) {
      event.preventDefault(); releaseCharge();
    }
  });
  window.addEventListener('blur', cancelActiveInputs);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) cancelActiveInputs();
  });
  window.addEventListener('resize', resizeArenaCanvas);
  window.addEventListener('beforeunload', () => network.dispose());

  function frame(now: number): void {
    processHeldControls(now);
    updateChargedPower(now);
    drawArena(now);
    requestAnimationFrame(frame);
  }

  window.setInterval(updateGameHud, 150);
  window.setInterval(() => network.ping(), 15_000);
  window.setInterval(() => {
    if (!room && entryTab === 'public') network.requestPublicRooms();
  }, 4_000);
  updateConnection();
  renderSurface(true);
  if (incomingRoom) network.resumeRoom(incomingRoom, savedPartyNickname());
  requestAnimationFrame(frame);
}
